import { Ban, CreditCard, Eye, ReceiptText, UserRound } from 'lucide-react';

import { formatCurrency } from '../../../../utils/formatCurrency';

import { PendingCashierSaleCardDTO } from '../../../../types/sale/dto/PendingCashierSaleCardDTO';

interface Props {
  sale: PendingCashierSaleCardDTO;

  onViewSale: (saleId: number) => void;

  onProcessSale: (sale: PendingCashierSaleCardDTO) => void;

  canCancel?: boolean;

  onCancelSale?: (sale: PendingCashierSaleCardDTO) => void;
}

const CashPendingSaleCard = ({
  sale,
  onViewSale,
  onProcessSale,
  canCancel = false,
  onCancelSale,
}: Props) => {
  return (
    <div
      className="
        flex flex-col overflow-hidden

        rounded-2xl border border-slate-200 bg-white

        shadow-sm transition-all

        hover:-translate-y-0.5 hover:shadow-md

        dark:border-strokedark dark:bg-boxdark
      "
    >
      {/* ===================================================== */}
      {/* 🔹 Encabezado */}
      {/* ===================================================== */}

      <div className="border-b border-slate-100 p-5 dark:border-strokedark">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2">
              <ReceiptText className="h-4 w-4 shrink-0 text-primary" />
              <h3 className="truncate text-base font-semibold text-slate-800 dark:text-white">
                {sale.documentNumber}
              </h3>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              {sale.documentType || 'Documento'}
            </p>
          </div>

          <span
            className="
              shrink-0 rounded-full bg-warning/10 px-2.5 py-1

              text-[11px] font-semibold uppercase tracking-wide text-warning
            "
          >
            {sale.status}
          </span>
        </div>

        {/* Total destacado */}
        <div className="mt-4">
          <p className="text-xs uppercase tracking-wide text-slate-400">
            Total a cobrar
          </p>
          <p className="text-3xl font-extrabold text-primary">
            ${formatCurrency(sale.total)}
          </p>
        </div>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Cliente / vendedor */}
      {/* ===================================================== */}

      <div className="space-y-2 px-5 py-4 text-sm">
        <div className="flex items-center gap-2">
          <UserRound className="h-4 w-4 shrink-0 text-slate-400" />
          <span className="text-slate-500 dark:text-slate-300">Cliente:</span>
          <span className="truncate font-semibold text-slate-800 dark:text-white">
            {sale.customerName}
          </span>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span>Vendedor:</span>
          <span className="truncate">{sale.sellerName || 'Sin vendedor'}</span>
        </div>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Acciones */}
      {/* ===================================================== */}

      <div className="mt-auto space-y-2 p-5 pt-0">
        <button
          onClick={() => onProcessSale(sale)}
          className="
            inline-flex w-full items-center justify-center gap-2

            rounded-xl bg-primary px-4 py-3

            text-sm font-semibold text-white

            transition-all hover:bg-opacity-90
          "
        >
          <CreditCard className="h-4 w-4" />
          Cobrar
        </button>

        <div className="flex gap-2">
          <button
            onClick={() => onViewSale(sale.saleId)}
            className="
              inline-flex flex-1 items-center justify-center gap-2

              rounded-xl border border-slate-200 px-3 py-2

              text-sm font-medium text-slate-600 transition-all

              hover:border-primary hover:text-primary

              dark:border-strokedark dark:text-slate-300
            "
          >
            <Eye className="h-4 w-4" />
            Ver
          </button>

          {canCancel && onCancelSale && (
            <button
              onClick={() => onCancelSale(sale)}
              title="Anular venta pendiente de cobro"
              className="
                inline-flex items-center justify-center gap-2

                rounded-xl border border-danger/30 px-3 py-2

                text-sm font-medium text-danger transition-all

                hover:bg-danger/10
              "
            >
              <Ban className="h-4 w-4" />
              Anular
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashPendingSaleCard;
